import {
  addDaysToDateString,
  applyStudentAssignmentTiming,
  getStudentAssignmentDueDate,
  getStudentAssignmentOpenDate
} from './assignmentTiming';

import {
  expandStudentAssignmentScheduleOccurrences,
  getScheduleItemSource,
  isScheduleChoiceEnabledForAssignment,
  loadStudentAssignmentScheduleContext
} from './studentAssignmentSchedule';


const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function headers() {
  return {
    apikey:
      SUPABASE_KEY,

    Authorization:
      'Bearer ' +
      SUPABASE_KEY
  };
}


async function readJson(
  response,
  label
) {
  const body =
    await response.text();

  if (!response.ok) {
    throw new Error(
      label +
      ' failed (' +
      response.status +
      '): ' +
      (
        body ||
        response.statusText
      )
    );
  }

  return body
    ? JSON.parse(body)
    : null;
}


function rows(value) {
  return Array.isArray(value)
    ? value
    : [];
}


function uniqueIds(values) {
  return [
    ...new Set(
      rows(values)
        .filter(Boolean)
        .map(String)
    )
  ];
}


function inFilter(values) {
  const ids =
    uniqueIds(values);

  if (!ids.length) {
    return null;
  }

  return (
    '(' +
    ids
      .map(encodeURIComponent)
      .join(',') +
    ')'
  );
}


function settingKey(
  courseId,
  assignmentTypeId
) {
  return [
    String(courseId || ''),
    String(assignmentTypeId || '')
  ].join('|');
}


function assignmentGroupKey(
  assignmentId,
  groupNumber
) {
  return [
    String(assignmentId || ''),
    String(groupNumber || '')
  ].join('|');
}


function latestByDate(
  sourceRows
) {
  return rows(sourceRows)
    .slice()
    .sort(
      (a, b) => {
        const aTime =
          new Date(
            a?.submitted_at ||
            a?.updated_at ||
            a?.created_at ||
            0
          ).getTime();

        const bTime =
          new Date(
            b?.submitted_at ||
            b?.updated_at ||
            b?.created_at ||
            0
          ).getTime();

        return bTime - aTime;
      }
    )[0] || null;
}


export function latestProgressFeedback(
  submission
) {
  const feedback =
    submission
      ?.assignment_feedback;

  if (!feedback) {
    return null;
  }

  const feedbackRows =
    Array.isArray(feedback)
      ? feedback
      : [feedback];

  return latestByDate(
    feedbackRows
  );
}


export function isProgressSubmissionComplete(submission, feedback) {
  if (submission?.submitted !== true) return false;

  const published = (Array.isArray(feedback) ? feedback : [feedback])
    .filter(row => row?.submitted === true)
    .sort((a, b) =>
      new Date(b.submitted_at || b.updated_at || 0).getTime() -
      new Date(a.submitted_at || a.updated_at || 0).getTime()
    )[0];

  return String(published?.completion_status || '')
    .trim().toLowerCase() !== 'incomplete';
}

export function applyProgressAssignmentDisplayNames(
  assignments,
  assignmentTypeSettings
) {
  const displayNameByType =
    new Map(
      rows(
        assignmentTypeSettings
      ).map(
        setting => [
          String(
            setting
              .assignment_type_id
          ),

          String(
            setting
              .display_name ||
            ''
          ).trim()
        ]
      )
    );

  return rows(assignments)
    .map(
      assignment => {
        const displayName =
          displayNameByType.get(
            String(
              assignment
                .assignment_type_id
            )
          );

        if (!displayName) {
          return assignment;
        }

        return {
          ...assignment,

          assignment_types: {
            ...(
              assignment
                .assignment_types ||
              {}
            ),

            name:
              displayName
          }
        };
      }
    );
}


export async function loadProgressAssignmentContext({
  courseId,
  enrolments = []
}) {
  const normalizedEnrolments =
    rows(enrolments)
      .filter(
        enrolment =>
          enrolment?.id
      )
      .map(
        enrolment => ({
          ...enrolment,

          course_id:
            enrolment.course_id ||
            enrolment?.courses?.id ||
            courseId
        })
      );

  const enrolmentIds =
    uniqueIds(
      normalizedEnrolments
        .map(
          enrolment =>
            enrolment.id
        )
    );

  const enrolmentFilter =
    inFilter(
      enrolmentIds
    );

  const membershipSuffix =
    enrolmentFilter
      ? (
          '&enrolment_id=in.' +
          enrolmentFilter
        )
      : '';

  const [
    assignmentResponse,
    settingResponse,
    membershipResponse,
    groupDeadlineResponse,
    structuredInstanceResponse,
    structuredWorkspaceResponse,
    scheduleSelectionResponse,
    scheduleContext
  ] =
    await Promise.all([
      fetch(
        SUPABASE_URL +
          '/rest/v1/course_assignments' +
          '?course_id=eq.' +
          encodeURIComponent(
            courseId
          ) +
          '&select=*,' +
          'assignment_types(' +
          'id,name,requires_submission,' +
          'is_core,assignment_kind' +
          ')' +
          '&order=instance_number.asc',
        {
          headers:
            headers()
        }
      ),

      fetch(
        SUPABASE_URL +
          '/rest/v1/course_assignment_type_settings' +
          '?course_id=eq.' +
          encodeURIComponent(
            courseId
          ) +
          '&select=*',
        {
          headers:
            headers()
        }
      ),

      fetch(
        SUPABASE_URL +
          '/rest/v1/assignment_group_memberships' +
          '?select=' +
          'course_assignment_id,' +
          'enrolment_id,' +
          'group_number,' +
          'course_assignments!inner(course_id)' +
          '&course_assignments.course_id=eq.' +
          encodeURIComponent(
            courseId
          ) +
          membershipSuffix,
        {
          headers:
            headers()
        }
      ),

      fetch(
        SUPABASE_URL +
          '/rest/v1/course_assignment_group_deadlines' +
          '?select=*,' +
          'course_assignments!inner(course_id)' +
          '&course_assignments.course_id=eq.' +
          encodeURIComponent(
            courseId
          ),
        {
          headers:
            headers()
        }
      ),

      fetch(
        SUPABASE_URL +
          '/rest/v1/course_structured_instances' +
          '?select=*,' +
          'course_assignments!inner(course_id)' +
          '&course_assignments.course_id=eq.' +
          encodeURIComponent(
            courseId
          ) +
          '&order=instance_order.asc',
        {
          headers:
            headers()
        }
      ),

      fetch(
        SUPABASE_URL +
          '/rest/v1/structured_form_group_workspaces' +
          '?select=' +
          'id,course_assignment_id,' +
          'group_number,' +
          'owner_enrolment_id,' +
          'working_submission_id,' +
          'course_assignments!inner(course_id)' +
          '&course_assignments.course_id=eq.' +
          encodeURIComponent(
            courseId
          ),
        {
          headers:
            headers()
        }
      ),

      fetch(
        SUPABASE_URL +
          '/rest/v1/course_assignment_schedule_selections' +
          '?select=' +
          'course_assignment_id,' +
          'course_assignment_schedule_item_id,' +
          'enrolment_id,' +
          'group_number,' +
          'course_assignments!inner(course_id)' +
          '&course_assignments.course_id=eq.' +
          encodeURIComponent(
            courseId
          ),
        {
          headers:
            headers()
        }
      ),

      loadStudentAssignmentScheduleContext({
        courseIds: [
          courseId
        ],

        enrolments: [],

        includeChoiceState:
          false
      })
    ]);

  const [
    assignmentRaw,
    settingRaw,
    membershipRaw,
    groupDeadlineRaw,
    structuredInstanceRaw,
    structuredWorkspaceRaw,
    scheduleSelectionRaw
  ] =
    await Promise.all([
      readJson(
        assignmentResponse,
        'Loading progress assignments'
      ),

      readJson(
        settingResponse,
        'Loading progress assignment settings'
      ),

      readJson(
        membershipResponse,
        'Loading progress group membership'
      ),

      readJson(
        groupDeadlineResponse,
        'Loading progress group deadlines'
      ),

      readJson(
        structuredInstanceResponse,
        'Loading progress Structured Form instances'
      ),

      readJson(
        structuredWorkspaceResponse,
        'Loading progress Structured Form workspaces'
      ),

      readJson(
        scheduleSelectionResponse,
        'Loading progress Schedule selections'
      )
    ]);

  const assignmentTypeSettings =
    rows(settingRaw);

  const groupMemberships =
    rows(membershipRaw);

  const assignments =
    applyProgressAssignmentDisplayNames(
      assignmentRaw,
      assignmentTypeSettings
    );

  const structuredInstances =
    rows(
      structuredInstanceRaw
    );

  let structuredGroupDeadlines =
    [];

  let structuredScheduleItems =
    [];

  let structuredScheduleSelections =
    [];

  const instanceFilter =
    inFilter(
      structuredInstances
        .map(
          instance =>
            instance.id
        )
    );

  if (instanceFilter) {
    const [
      structuredDeadlineResponse,
      structuredItemResponse,
      structuredSelectionResponse
    ] =
      await Promise.all([
        fetch(
          SUPABASE_URL +
            '/rest/v1/' +
            'course_structured_instance_group_deadlines' +
            '?course_structured_instance_id=in.' +
            instanceFilter +
            '&select=*',
          {
            headers:
              headers()
          }
        ),

        fetch(
          SUPABASE_URL +
            '/rest/v1/' +
            'course_structured_instance_schedule_items' +
            '?course_structured_instance_id=in.' +
            instanceFilter +
            '&select=*' +
            '&order=display_order.asc,created_at.asc',
          {
            headers:
              headers()
          }
        ),

        fetch(
          SUPABASE_URL +
            '/rest/v1/' +
            'course_structured_instance_schedule_selections' +
            '?course_structured_instance_id=in.' +
            instanceFilter +
            '&select=*',
          {
            headers:
              headers()
          }
        )
      ]);

    structuredGroupDeadlines =
      rows(
        await readJson(
          structuredDeadlineResponse,
          'Loading progress Structured Form group deadlines'
        )
      );

    structuredScheduleItems =
      rows(
        await readJson(
          structuredItemResponse,
          'Loading progress Structured Form Schedule items'
        )
      );

    structuredScheduleSelections =
      rows(
        await readJson(
          structuredSelectionResponse,
          'Loading progress Structured Form Schedule selections'
        )
      );
  }

  const requestedEnrolmentIds =
    new Set(
      enrolmentIds.map(String)
    );

  const expandedChoiceSelections =
    [];

  rows(
    scheduleSelectionRaw
  ).forEach(
    selection => {
      if (
        selection
          .enrolment_id
      ) {
        if (
          requestedEnrolmentIds
            .has(
              String(
                selection
                  .enrolment_id
              )
            )
        ) {
          expandedChoiceSelections
            .push({
              ...selection,

              context_enrolment_id:
                selection
                  .enrolment_id,

              context_course_id:
                courseId
            });
        }

        return;
      }

      if (
        selection
          .group_number ===
          null ||
        selection
          .group_number ===
          undefined
      ) {
        return;
      }

      groupMemberships
        .filter(
          membership =>
            String(
              membership
                .course_assignment_id
            ) ===
              String(
                selection
                  .course_assignment_id
              ) &&
            Number(
              membership
                .group_number
            ) ===
              Number(
                selection
                  .group_number
              )
        )
        .forEach(
          membership => {
            expandedChoiceSelections
              .push({
                ...selection,

                context_enrolment_id:
                  membership
                    .enrolment_id,

                context_course_id:
                  courseId
              });
          }
        );
    }
  );

  return {
    courseId,

    assignments,

    assignmentTypeSettings,

    groupMemberships,

    groupDeadlines:
      rows(
        groupDeadlineRaw
      ),

    structuredInstances,

    structuredGroupWorkspaces:
      rows(
        structuredWorkspaceRaw
      ),

    structuredGroupDeadlines,

    structuredScheduleItems,

    structuredScheduleSelections,

    scheduleContext: {
      ...(
        scheduleContext ||
        {}
      ),

      choiceSelections:
        expandedChoiceSelections
    }
  };
}


export function collectProgressSubmissionEnrolmentIds({
  enrolments,
  context
}) {
  const enrolmentRows =
    rows(enrolments)
      .filter(
        enrolment =>
          enrolment?.id
      );

  const relevantIds =
    new Set(
      enrolmentRows
        .map(
          enrolment =>
            String(
              enrolment.id
            )
        )
    );

  const membershipRows =
    rows(
      context
        ?.groupMemberships
    );

  const workspaceByAssignmentAndGroup =
    new Map(
      rows(
        context
          ?.structuredGroupWorkspaces
      ).map(
        workspace => [
          assignmentGroupKey(
            workspace
              .course_assignment_id,
            workspace
              .group_number
          ),

          workspace
        ]
      )
    );

  membershipRows
    .filter(
      membership =>
        relevantIds.has(
          String(
            membership
              .enrolment_id
          )
        )
    )
    .forEach(
      membership => {
        const workspace =
          workspaceByAssignmentAndGroup
            .get(
              assignmentGroupKey(
                membership
                  .course_assignment_id,

                membership
                  .group_number
              )
            );

        if (
          workspace
            ?.owner_enrolment_id
        ) {
          relevantIds.add(
            String(
              workspace
                .owner_enrolment_id
            )
          );
        }
      }
    );

  return [
    ...relevantIds
  ];
}


export async function fetchProgressSubmissionData(
  enrolmentIds
) {
  const filter =
    inFilter(
      enrolmentIds
    );

  if (!filter) {
    return {
      submissions: [],
      completions: []
    };
  }

  const [
    submissionResponse,
    completionResponse
  ] =
    await Promise.all([
      fetch(
        SUPABASE_URL +
          '/rest/v1/assignment_submissions' +
          '?enrolment_id=in.' +
          filter +
          '&select=*,' +
          'assignment_feedback(*)',
        {
          headers:
            headers()
        }
      ),

      fetch(
        SUPABASE_URL +
          '/rest/v1/assignment_completions' +
          '?enrolment_id=in.' +
          filter +
          '&select=*',
        {
          headers:
            headers()
        }
      )
    ]);

  return {
    submissions:
      rows(
        await readJson(
          submissionResponse,
          'Loading progress assignment submissions'
        )
      ),

    completions:
      rows(
        await readJson(
          completionResponse,
          'Loading progress assignment completions'
        )
      )
  };
}


export async function fetchProgressCqManualMarks(
  enrolmentIds
) {
  const filter =
    inFilter(
      enrolmentIds
    );

  if (!filter) {
    return [];
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/cq_manual_submission_marks' +
        '?enrolment_id=in.' +
        filter +
        '&select=*',
      {
        headers:
          headers()
      }
    );

  /*
   * The manual-CQ table was added later than
   * Student Progress. If a deployment has not
   * installed it yet, Progress should continue
   * to work rather than fail completely.
   */
  if (!response.ok) {
    return [];
  }

  return rows(
    await response.json()
  );
}


function getParentSubmission(
  submissions,
  enrolmentId,
  assignmentId
) {
  return rows(submissions)
    .find(
      submission =>
        String(
          submission
            .enrolment_id
        ) ===
          String(
            enrolmentId
          ) &&
        String(
          submission
            .course_assignment_id
        ) ===
          String(
            assignmentId
          ) &&
        !submission
          .course_assignment_schedule_item_id &&
        !submission
          .structured_form_instance_id
    ) || null;
}


function getOccurrenceSubmission(
  submissions,
  enrolmentId,
  assignmentId,
  scheduleItemId
) {
  return rows(submissions)
    .find(
      submission =>
        String(
          submission
            .enrolment_id
        ) ===
          String(
            enrolmentId
          ) &&
        String(
          submission
            .course_assignment_id
        ) ===
          String(
            assignmentId
          ) &&
        String(
          submission
            .course_assignment_schedule_item_id
        ) ===
          String(
            scheduleItemId
          )
    ) || null;
}


function getStructuredSubmission(
  submissions,
  enrolmentId,
  assignmentId,
  instanceId
) {
  return rows(submissions)
    .find(
      submission =>
        String(
          submission
            .enrolment_id
        ) ===
          String(
            enrolmentId
          ) &&
        String(
          submission
            .course_assignment_id
        ) ===
          String(
            assignmentId
          ) &&
        String(
          submission
            .structured_form_instance_id
        ) ===
          String(
            instanceId
          )
    ) || null;
}


function usesAssignmentGroups(
  assignment
) {
  return (
    assignment
      ?.groups_enabled ===
      true &&
    Number(
      assignment
        ?.group_count
    ) > 0
  );
}


export function buildProgressAssessmentUnits({
  courseId,
  enrolment,
  context,
  submissions = [],
  completions = []
}) {
  if (
    !context ||
    !enrolment?.id
  ) {
    return [];
  }

  const normalizedEnrolment = {
    ...enrolment,

    course_id:
      enrolment.course_id ||
      enrolment?.courses?.id ||
      courseId
  };

  const assignmentTypeSettings =
    rows(
      context
        .assignmentTypeSettings
    );

  const settingsByType =
    new Map(
      assignmentTypeSettings
        .map(
          setting => [
            settingKey(
              courseId,
              setting
                .assignment_type_id
            ),

            setting
          ]
        )
    );

  const membershipByAssignment =
    new Map(
      rows(
        context
          .groupMemberships
      )
        .filter(
          membership =>
            String(
              membership
                .enrolment_id
            ) ===
              String(
                normalizedEnrolment.id
              )
        )
        .map(
          membership => [
            String(
              membership
                .course_assignment_id
            ),

            membership
          ]
        )
    );

  const workspaceByAssignmentAndGroup =
    new Map(
      rows(
        context
          .structuredGroupWorkspaces
      ).map(
        workspace => [
          assignmentGroupKey(
            workspace
              .course_assignment_id,

            workspace
              .group_number
          ),

          workspace
        ]
      )
    );

  const assignments =
    rows(
      context.assignments
    )
      .filter(
        assignment =>
          assignment
            ?.assignment_types
            ?.is_core !==
          true
      );

  const units =
    [];

  const settingFor =
    assignment =>
      settingsByType.get(
        settingKey(
          courseId,
          assignment
            .assignment_type_id
        )
      ) || null;

  const studentCanUseAssignment =
    assignment => {
      if (
        !usesAssignmentGroups(
          assignment
        )
      ) {
        return true;
      }

      return membershipByAssignment
        .has(
          String(
            assignment.id
          )
        );
    };


  // ----------------------------------------------------------
  // Non-submission assignments
  // ----------------------------------------------------------

  assignments
    .filter(
      assignment =>
        (
          assignment
            ?.assignment_types
            ?.requires_submission ??
          true
        ) ===
          false
    )
    .filter(
      studentCanUseAssignment
    )
    .forEach(
      assignment => {
        const completion =
          rows(completions)
            .find(
              row =>
                String(
                  row
                    .enrolment_id
                ) ===
                  String(
                    normalizedEnrolment
                      .id
                  ) &&
                String(
                  row
                    .course_assignment_id
                ) ===
                  String(
                    assignment.id
                  )
            ) || null;

        units.push({
          key:
            String(
              assignment.id
            ) +
            '|completion',

          kind:
            'completion',

          requiresSubmission:
            false,

          assignmentId:
            assignment.id,

          assignmentTypeId:
            assignment
              .assignment_type_id,

          typeId:
            assignment
              .assignment_type_id,

          typeName:
            assignment
              ?.assignment_types
              ?.name ||
            'Assignment',

          assignment,

          dueDate:
            assignment
              .occurs_on ||
            null,

          openDate:
            null,

          completed:
            completion
              ?.completed ===
            true,

          submitted:
            completion
              ?.completed ===
            true,

          completion,

          submission:
            null,

          feedback:
            null,

          detailLabel:
            'Completion'
        });
      }
    );


  // ----------------------------------------------------------
  // Ordinary submission assignments
  // ----------------------------------------------------------

  const ordinaryAssignments =
    assignments
      .filter(
        assignment =>
          (
            assignment
              ?.assignment_types
              ?.requires_submission ??
            true
          ) !==
            false
      )
      .filter(
        assignment =>
          assignment
            ?.assignment_types
            ?.assignment_kind !==
          'structured_form'
      )
      .filter(
        studentCanUseAssignment
      );

  const timedOrdinaryAssignments =
    applyStudentAssignmentTiming({
      assignments:
        ordinaryAssignments,

      assignmentTypeSettings,

      enrolments: [
        normalizedEnrolment
      ],

      memberships:
        rows(
          context
            .groupMemberships
        ),

      deadlines:
        rows(
          context
            .groupDeadlines
        ),

      submissions:
        rows(submissions)
    })
      .filter(
        assignment =>
          assignment
            .student_assignment_applicable !==
          false
      );

  const expandedOrdinaryAssignments =
    expandStudentAssignmentScheduleOccurrences({
      assignments:
        timedOrdinaryAssignments,

      assignmentTypeSettings,

      enrolments: [
        normalizedEnrolment
      ],

      memberships:
        rows(
          context
            .groupMemberships
        ),

      submissions:
        rows(submissions),

      scheduleContext:
        context
          .scheduleContext ||
        {},

      isMaster:
        false
    });

  timedOrdinaryAssignments
    .forEach(
      assignment => {
        const setting =
          settingFor(
            assignment
          );

        const scheduleEnabled =
          setting
            ?.schedule_enabled ===
          true;

        const choiceEnabled =
          scheduleEnabled &&
          isScheduleChoiceEnabledForAssignment({
            assignmentId:
              assignment.id,

            setting,

            context:
              context
                .scheduleContext ||
              {},

            isMaster:
              false
          });

        const occurrences =
          rows(
            expandedOrdinaryAssignments
          )
            .filter(
              occurrence =>
                String(
                  occurrence.id
                ) ===
                  String(
                    assignment.id
                  ) &&
                occurrence
                  .student_assignment_applicable !==
                  false
            );

        occurrences
          .forEach(
            occurrence => {
              let submission =
                null;

              if (
                occurrence
                  .course_assignment_schedule_item_id
              ) {
                submission =
                  getOccurrenceSubmission(
                    submissions,
                    normalizedEnrolment.id,
                    assignment.id,
                    occurrence
                      .course_assignment_schedule_item_id
                  );
              } else {
                submission =
                  getParentSubmission(
                    submissions,
                    normalizedEnrolment.id,
                    assignment.id
                  );

                /*
                 * A damaged historic Schedule link should not
                 * erase work which still exists in an occurrence
                 * submission row.
                 */
                if (
                  !submission &&
                  scheduleEnabled &&
                  occurrence
                    .student_assignment_timing_source ===
                    'schedule_fallback'
                ) {
                  submission =
                    latestByDate(
                      rows(submissions)
                        .filter(
                          row =>
                            String(
                              row.enrolment_id
                            ) ===
                              String(
                                normalizedEnrolment.id
                              ) &&
                            String(
                              row.course_assignment_id
                            ) ===
                              String(
                                assignment.id
                              ) &&
                            !!row
                              .course_assignment_schedule_item_id
                        )
                    );
                }
              }

              const feedback =
                latestProgressFeedback(
                  submission
                );

              const detailLabel =
                [
                  occurrence
                    .schedule_source_title,

                  occurrence
                    .schedule_source_meta
                ]
                  .filter(Boolean)
                  .join(' · ') ||
                'Assignment';

              units.push({
                key:
                  occurrence
                    .student_assignment_occurrence_key ||
                  (
                    String(
                      assignment.id
                    ) +
                    '|' +
                    String(
                      occurrence
                        .course_assignment_schedule_item_id ||
                      'base'
                    )
                  ),

                kind:
                  occurrence
                    .schedule_occurrence
                    ? 'scheduled'
                    : 'assignment',

                requiresSubmission:
                  true,

                assignmentId:
                  assignment.id,

                assignmentTypeId:
                  assignment
                    .assignment_type_id,

                typeId:
                  assignment
                    .assignment_type_id,

                typeName:
                  assignment
                    ?.assignment_types
                    ?.name ||
                  'Assignment',

                assignment,

                occurrence,

                scheduleItemId:
                  occurrence
                    .course_assignment_schedule_item_id ||
                  null,

                openDate:
                  getStudentAssignmentOpenDate(
                    occurrence
                  ),

                dueDate:
                  getStudentAssignmentDueDate(
                    occurrence
                  ),

                detailLabel,

                submission,

                feedback,

                submitted:
                  submission
                    ?.submitted ===
                  true,

                completed:
                  isProgressSubmissionComplete(submission, feedback)
              });
            }
          );

        if (choiceEnabled) {
          const requiredCount =
            Math.max(
              1,
              Number(
                setting
                  ?.schedule_choice_required_count
              ) || 1
            );

          const missingCount =
            Math.max(
              0,
              requiredCount -
                occurrences.length
            );

          for (
            let index = 0;
            index < missingCount;
            index++
          ) {
            units.push({
              key:
                String(
                  assignment.id
                ) +
                '|choice-missing-' +
                String(index + 1),

              kind:
                'scheduled_choice_missing',

              requiresSubmission:
                true,

              assignmentId:
                assignment.id,

              assignmentTypeId:
                assignment
                  .assignment_type_id,

              typeId:
                assignment
                  .assignment_type_id,

              typeName:
                assignment
                  ?.assignment_types
                  ?.name ||
                'Assignment',

              assignment,

              occurrence:
                null,

              scheduleItemId:
                null,

              openDate:
                null,

              dueDate:
                null,

              detailLabel:
                'Schedule choice not selected',

              submission:
                null,

              feedback:
                null,

              submitted:
                false,

              completed:
                false,

              choiceMissing:
                true
            });
          }
        }
      }
    );


  // ----------------------------------------------------------
  // Structured Forms
  // ----------------------------------------------------------

  assignments
    .filter(
      assignment =>
        (
          assignment
            ?.assignment_types
            ?.requires_submission ??
          true
        ) !==
          false
    )
    .filter(
      assignment =>
        assignment
          ?.assignment_types
          ?.assignment_kind ===
        'structured_form'
    )
    .filter(
      studentCanUseAssignment
    )
    .forEach(
      assignment => {
        const setting =
          settingFor(
            assignment
          ) || {};

        const membership =
          membershipByAssignment
            .get(
              String(
                assignment.id
              )
            ) || null;

        const groupNumber =
          usesAssignmentGroups(
            assignment
          )
            ? (
                membership
                  ?.group_number ??
                null
              )
            : null;

        const workspace =
          groupNumber !== null &&
          groupNumber !== undefined
            ? (
                workspaceByAssignmentAndGroup
                  .get(
                    assignmentGroupKey(
                      assignment.id,
                      groupNumber
                    )
                  ) ||
                null
              )
            : null;

        const ownerEnrolmentId =
          workspace
            ?.owner_enrolment_id ||
          normalizedEnrolment.id;

        const parentSubmission =
          getParentSubmission(
            submissions,
            ownerEnrolmentId,
            assignment.id
          );

        const extensionDays =
          Math.max(
            0,
            Number(
              parentSubmission
                ?.extension_days
            ) || 0
          );

        const scheduleEnabled =
          setting
            .schedule_enabled ===
          true;

        const choiceEnabled =
          scheduleEnabled &&
          setting
            .schedule_choice_enabled ===
          true;

        rows(
          context
            .structuredInstances
        )
          .filter(
            instance =>
              String(
                instance
                  .course_assignment_id
              ) ===
                String(
                  assignment.id
                )
          )
          .forEach(
            instance => {
              let openDate =
                null;

              let baseDueDate =
                null;

              let effectiveDueDate =
                null;

              let scheduleItem =
                null;

              let source = {
                title: '',
                meta: '',
                anchorDate:
                  null
              };

              let choiceMissing =
                false;

              if (scheduleEnabled) {
                const mode =
                  choiceEnabled
                    ? 'choice'
                    : 'single';

                const allItems =
                  rows(
                    context
                      .structuredScheduleItems
                  )
                    .filter(
                      item =>
                        String(
                          item
                            .course_structured_instance_id
                        ) ===
                          String(
                            instance.id
                          )
                    )
                    .sort(
                      (a, b) =>
                        (
                          Number(
                            a.display_order
                          ) || 0
                        ) -
                          (
                            Number(
                              b.display_order
                            ) || 0
                          ) ||
                        String(a.id)
                          .localeCompare(
                            String(b.id)
                          )
                    );

                let activeItems =
                  allItems
                    .filter(
                      item =>
                        item
                          .configuration_mode ===
                        mode
                    );

                if (
                  !choiceEnabled &&
                  activeItems.length ===
                    0 &&
                  allItems.length ===
                    1
                ) {
                  activeItems =
                    allItems;
                }

                if (choiceEnabled) {
                  let selection =
                    null;

                  if (
                    groupNumber !==
                      null &&
                    groupNumber !==
                      undefined
                  ) {
                    selection =
                      rows(
                        context
                          .structuredScheduleSelections
                      )
                        .find(
                          row =>
                            String(
                              row
                                .course_structured_instance_id
                            ) ===
                              String(
                                instance.id
                              ) &&
                            row
                              .enrolment_id ===
                              null &&
                            Number(
                              row
                                .group_number
                            ) ===
                              Number(
                                groupNumber
                              )
                        ) ||
                      null;
                  } else {
                    selection =
                      rows(
                        context
                          .structuredScheduleSelections
                      )
                        .find(
                          row =>
                            String(
                              row
                                .course_structured_instance_id
                            ) ===
                              String(
                                instance.id
                              ) &&
                            String(
                              row
                                .enrolment_id
                            ) ===
                              String(
                                normalizedEnrolment.id
                              ) &&
                            row
                              .group_number ===
                              null
                        ) ||
                      null;
                  }

                  if (selection) {
                    scheduleItem =
                      activeItems
                        .find(
                          item =>
                            String(
                              item.id
                            ) ===
                              String(
                                selection
                                  .course_structured_instance_schedule_item_id
                              )
                        ) ||
                      null;
                  }

                  choiceMissing =
                    !scheduleItem;
                } else {
                  scheduleItem =
                    activeItems[0] ||
                    null;
                }

                source =
                  getScheduleItemSource(
                    scheduleItem,
                    context
                      .scheduleContext ||
                    {}
                  );

                openDate =
                  addDaysToDateString(
                    source
                      ?.anchorDate,
                    instance
                      .schedule_open_offset_days
                  );

                baseDueDate =
                  addDaysToDateString(
                    source
                      ?.anchorDate,
                    instance
                      .schedule_close_offset_days
                  );

                effectiveDueDate =
                  addDaysToDateString(
                    baseDueDate,
                    extensionDays
                  );
              } else {
                const groupDeadline =
                  groupNumber !== null &&
                  groupNumber !== undefined &&
                  setting
                    .group_deadlines_enabled ===
                    true
                    ? (
                        rows(
                          context
                            .structuredGroupDeadlines
                        )
                          .find(
                            row =>
                              String(
                                row
                                  .course_structured_instance_id
                              ) ===
                                String(
                                  instance.id
                                ) &&
                              Number(
                                row
                                  .group_number
                              ) ===
                                Number(
                                  groupNumber
                                )
                          ) ||
                        null
                      )
                    : null;

                openDate =
                  groupDeadline
                    ?.open_date ||
                  instance
                    .open_date ||
                  null;

                baseDueDate =
                  groupDeadline
                    ?.close_date ||
                  instance
                    .close_date ||
                  null;

                effectiveDueDate =
                  addDaysToDateString(
                    baseDueDate,
                    extensionDays
                  );
              }

              const submission =
                getStructuredSubmission(
                  submissions,
                  ownerEnrolmentId,
                  assignment.id,
                  instance.id
                );

              const feedback =
                latestProgressFeedback(
                  submission
                );

              const scheduleLabel =
                [
                  source?.title,
                  source?.meta
                ]
                  .filter(Boolean)
                  .join(' · ');

              units.push({
                key:
                  String(
                    assignment.id
                  ) +
                  '|structured|' +
                  String(
                    instance.id
                  ),

                kind:
                  'structured_form',

                requiresSubmission:
                  true,

                assignmentId:
                  assignment.id,

                assignmentTypeId:
                  assignment
                    .assignment_type_id,

                typeId:
                  assignment
                    .assignment_type_id,

                typeName:
                  assignment
                    ?.assignment_types
                    ?.name ||
                  'Assignment',

                assignment,

                structuredInstance:
                  instance,

                structuredFormInstanceId:
                  instance.id,

                structuredFormInstanceOrder:
                  instance
                    .instance_order,

                submissionEnrolmentId:
                  ownerEnrolmentId,

                scheduleItemId:
                  scheduleItem
                    ?.id ||
                  null,

                openDate,

                baseDueDate,

                dueDate:
                  effectiveDueDate,

                detailLabel:
                  [
                    'Part ' +
                      String(
                        instance
                          .instance_order ||
                        ''
                      ),

                    scheduleLabel
                  ]
                    .filter(Boolean)
                    .join(' · '),

                choiceMissing,

                submission,

                feedback,

                submitted:
                  submission
                    ?.submitted ===
                  true,

                completed:
                  isProgressSubmissionComplete(submission, feedback)
              });
            }
          );
      }
    );

  return units;
}


export function summariseProgressUnits(
  units,
  today
) {
  const unitRows =
    rows(units);

  const isCompleted =
    unit =>
      unit
        ?.requiresSubmission ===
      false
        ? unit?.completed ===
          true
        : unit?.completed ===
          true;

  const completed =
    unitRows
      .filter(
        isCompleted
      )
      .length;

  const submitted =
    unitRows
      .filter(
        unit =>
          unit
            ?.requiresSubmission !==
            false &&
          unit
            ?.submitted ===
            true
      )
      .length;

  const graded =
    unitRows
      .filter(
        unit =>
          unit
            ?.requiresSubmission !==
            false &&
          unit
            ?.submitted ===
            true &&
          latestProgressFeedback(
            unit.submission
          )?.submitted ===
            true
      )
      .length;

  const due =
    unitRows
      .filter(
        unit =>
          !!unit?.dueDate &&
          String(
            unit.dueDate
          ).slice(0, 10) <=
            today
      )
      .length;

  const soFar =
    unitRows
      .filter(
        unit =>
          isCompleted(unit) ||
          (
            !!unit?.dueDate &&
            String(
              unit.dueDate
            ).slice(0, 10) <=
              today
          )
      )
      .length;

  return {
    total:
      unitRows.length,

    completed,

    submitted,

    graded,

    due,

    soFar
  };
}
